import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Loader2, X } from 'lucide-react';
import Modal from '../Modal';
import { ModalFormErrorBanner } from '../ModalFormErrorBanner';
import { BlockLoader } from '../loading';
import { probationAPI } from '../../services/api';
import { formatIsoDay } from '../../utils/dateHelpers';
import type {
  ProbationDecisionInput,
  ProbationDecisionResult,
  ProbationDecisionSnapshot,
  ProbationRecommendation,
  ProbationReviewBundle,
  ProbationView,
} from '../../types/probation';

/**
 * Screen 2 of Probation Confirmation: the review form for one employee.
 *
 * A section-by-section copy of the Employee Portal's EmployeeConfirmationDialog
 * (and the load/validation rules of its employeeConfirmationStore), so a manager
 * sees the same fields, steps and validation copy in either portal. The API
 * does the 1Office reads and builds the saveConfirmationScores payload; this
 * form only collects the decision. Deliberate differences from the Portal:
 *  - the success message is the outcome-specific one the API returns ("Sent to
 *    … for the final decision.", "…'s employment is confirmed." …), handed to
 *    the parent through onSubmitted, not the Portal's "Request has been approved";
 *  - the final (L2) step has ONE Extension Period control. The Portal also
 *    renders the L1 dropdown there, bound to the same value;
 *  - a last working date before today is refused (the Portal only bounds the
 *    picker). So a prefilled date — L1's at L2, or 1Office's suggestion — that
 *    falls outside today's bounds is not filled in; a note asks for a new one.
 *
 * `mode` comes from the API (who is asking + the request's stage):
 *  'l1'   — the reporting manager scores every skill, then recommends;
 *  'l2'   — the final approver sees L1's scores and decision read-only and
 *           records their own recommendation, prefilled with L1's;
 *  'view' — everything read-only, including the final decision once made.
 */

interface ProbationReviewModalProps {
  confirmationId: number;
  view: ProbationView;
  /** "<Name>-<EmpCode>" — the title until the details arrive. */
  employeeLabel: string;
  /**
   * No argument for a plain Cancel / Close / ×. WITH a message when the modal
   * closed itself because the request can't be worked on here any more (not
   * found, already actioned, changed since it was opened, or it failed to
   * load) — the list behind it is stale then, so the parent shows the message
   * and re-reads it.
   */
  onClose(message?: string): void;
  onSubmitted(result: ProbationDecisionResult): void;
}

const RECOMMENDATIONS: { id: ProbationRecommendation; label: string }[] = [
  { id: 1, label: 'Confirmation of employment' },
  { id: 2, label: 'Extension of employment' },
  { id: 3, label: 'Termination of employment' },
];
const EXTENSION_PERIODS = ['1', '2', '3'];
const EVALUATION_SCORES = ['1', '2', '3', '4'];
const SCORE_COLUMN_LABEL = 'Evaluation Score (0 to 4, 4 is best)';

// The API's own copy, used when a response carries no message of its own.
const NOT_YOURS_MESSAGE = "This request may already be actioned, or it isn't pending your approval.";
const STALE_MESSAGE = 'This confirmation changed since you opened it. Please reopen it.';
const SUBMIT_DISABLED_MESSAGE =
  'Submitting from ChannelPlex is switched off for now. Please use the Employee Portal to submit this confirmation.';
// The Employee Portal's copy.
const NO_SKILLS_MESSAGE = 'Please add the required skills to the budget.';
const LOAD_FAILED_MESSAGE = 'Unable to load employee confirmation details.';
const LAST_WORKING_DATE_FAILED_MESSAGE = 'Unable to load last working date.';
const SUBMIT_FAILED_MESSAGE = 'Unable to submit confirmation approval.';
// 502s after which 1Office may hold (part of) the decision: the API holds the step
// for a few minutes, so a second Submit from this form can only be refused. The
// manager reopens it later instead, as the message says.
const SENT_UNCONFIRMED_CODES = ['UPSTREAM_FAILED', 'UNKNOWN_OUTCOME', 'PARTIALLY_SAVED'];

const TH_BASE_CLASS = 'px-4 py-3 text-left text-xs font-semibold text-gray-600';
const TH_CLASS = `${TH_BASE_CLASS} whitespace-nowrap`;
const TD_CLASS = 'px-4 py-3 align-top text-sm text-gray-700';
// The skills tables stack each row (skill, score, comment) below `sm` instead of
// scrolling sideways, so nothing — a Comments box, an error — is off a phone screen.
// Each stacked cell names its column from data-label, as the headers are hidden.
const STACK_TABLE_CLASS = 'w-full sm:min-w-[36rem]';
const STACK_THEAD_CLASS = 'hidden sm:table-header-group';
const STACK_TBODY_CLASS = 'block divide-y divide-gray-100 sm:table-row-group';
const STACK_TR_CLASS = 'block space-y-2 px-4 py-3 sm:table-row sm:space-y-0 sm:p-0';
const STACK_EMPTY_TD_CLASS = 'block px-4 py-6 text-center text-sm text-gray-500 sm:table-cell';
const STACK_NAME_TD_CLASS =
  'block break-words text-sm font-medium text-gray-900 sm:table-cell sm:px-4 sm:pb-3 sm:align-top sm:font-normal sm:text-gray-700';
const STACK_TD_CLASS =
  'block text-sm text-gray-700 sm:table-cell sm:px-4 sm:py-3 sm:align-top before:mb-1 before:block before:text-xs before:font-medium before:text-gray-500 before:content-[attr(data-label)] sm:before:hidden';
const READ_ONLY_CLASS =
  'block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-100';
// An invalid control keeps a red ring on focus: the light primary ring is all but
// invisible on a red border, and the first invalid field is focused after Submit.
const controlClass = (hasError: boolean) =>
  `block w-full rounded-lg border px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
    hasError
      ? 'border-red-500 focus:border-red-500 focus:ring-red-300'
      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-100'
  }`;

interface SkillEvaluation {
  skillId: number;
  skillName: string;
  scoreId: string;
  comment: string;
}

interface FieldErrors {
  recommendation: string | null;
  extensionMonths: string | null;
  lastWorkingDate: string | null;
  feedback: string | null;
  skillScores: Record<number, string>;
  skillComments: Record<number, string>;
}

const NO_FIELD_ERRORS: FieldErrors = {
  recommendation: null,
  extensionMonths: null,
  lastWorkingDate: null,
  feedback: null,
  skillScores: {},
  skillComments: {},
};

const hasFieldErrors = (errors: FieldErrors) =>
  Boolean(errors.recommendation || errors.extensionMonths || errors.lastWorkingDate || errors.feedback) ||
  Object.keys(errors.skillScores).length > 0 ||
  Object.keys(errors.skillComments).length > 0;

// The 422 `errors` keys that have a field of their own: these four, plus
// `scores.<skillId>.scoreId|comments` for a row of the skills table. Anything
// else (a skill added to the budget since the form loaded, a malformed body)
// joins the message above the buttons.
const SERVER_FIELD_KEYS = ['recommendation', 'extensionMonths', 'lastWorkingDate', 'feedback'] as const;
type ServerFieldKey = (typeof SERVER_FIELD_KEYS)[number];
const SERVER_SKILL_KEY = /^scores\.(\d+)\.(scoreId|comments)$/;

interface ApiFailure {
  status: number | null;
  code: string | null;
  message: string | null;
  errors: Record<string, unknown> | null;
}

// The `{ success: false, code, message, errors? }` envelope off an axios error.
const readFailure = (err: unknown): ApiFailure => {
  const response = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
  const data = response?.data ?? {};
  return {
    status: response?.status ?? null,
    code: typeof data.code === 'string' ? data.code : null,
    message: typeof data.message === 'string' && data.message.trim() ? data.message : null,
    errors: data.errors && typeof data.errors === 'object' ? (data.errors as Record<string, unknown>) : null,
  };
};

const splitServerErrors = (errors: Record<string, unknown> | null, skills: SkillEvaluation[]) => {
  const fields: FieldErrors = { ...NO_FIELD_ERRORS, skillScores: {}, skillComments: {} };
  const other: string[] = [];
  for (const [key, value] of Object.entries(errors ?? {})) {
    const text = Array.isArray(value) ? value.join(' ') : String(value ?? '');
    if (!text.trim()) continue;
    if ((SERVER_FIELD_KEYS as readonly string[]).includes(key)) {
      fields[key as ServerFieldKey] = text;
      continue;
    }
    const skillMatch = SERVER_SKILL_KEY.exec(key);
    const index = skillMatch ? skills.findIndex((skill) => skill.skillId === Number(skillMatch[1])) : -1;
    if (skillMatch && index >= 0) {
      if (skillMatch[2] === 'scoreId') fields.skillScores[index] = text;
      else fields.skillComments[index] = text;
    } else {
      other.push(text);
    }
  }
  return { fields, other };
};

// 'YYYY-MM-DD' off a date or timestamp string; '' when there is none.
const toDay = (value: string | null | undefined) => /^\d{4}-\d{2}-\d{2}/.exec(value ?? '')?.[0] ?? '';

// Today in the browser's calendar, as the Portal bounds the date picker. Only
// a fallback: the API sends the bounds it will validate against.
const localToday = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const displayDay = (value: string | null | undefined) => (toDay(value) ? formatIsoDay(toDay(value)) : '—');

// A date the form may fill in for the manager: inside [min, max] (max optional).
const withinBounds = (day: string, min: string, max: string | undefined) =>
  Boolean(day) && day >= min && (max === undefined || day <= max);

const recommendationLabel = (value: number | string | null | undefined) => {
  if (value == null || value === '') return '—';
  return RECOMMENDATIONS.find((option) => String(option.id) === String(value))?.label ?? String(value);
};

const monthsLabel = (months: number | string) => `${months} month${Number(months) === 1 ? '' : 's'}`;

// Year and zero-based month off a KPI date. Read off the string where possible
// so a browser outside IST can't shift a date-only value into the month before.
const yearMonthOf = (value: string) => {
  const match = /^(\d{4})-(\d{2})/.exec(value);
  if (match) {
    const month = Number(match[2]) - 1;
    return month >= 0 && month < 12 ? { year: Number(match[1]), month } : null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : { year: date.getFullYear(), month: date.getMonth() };
};

// The Portal's kpiAchievement label: a one-month window reads as its month
// ("April 2026"), anything longer as the quarter it starts in ("AMJ-2026").
const QUARTERS = ['JFM', 'AMJ', 'JAS', 'OND'];
const kpiPeriodLabel = (validFrom: string | null, validTo: string | null) => {
  if (!validFrom || !validTo) return '—';
  const from = yearMonthOf(validFrom);
  const to = yearMonthOf(validTo);
  if (!from || !to) return '—';
  if (from.year === to.year && from.month === to.month) {
    return new Date(from.year, from.month, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }
  return `${QUARTERS[Math.floor(from.month / 3)]}-${from.year}`;
};

const describedBy = (...ids: (string | false | null | undefined)[]) => ids.filter(Boolean).join(' ') || undefined;

function FieldLabel({ htmlFor, label, required }: { htmlFor: string; label: string; required?: boolean }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
  );
}

function FieldError({ id, message }: { id: string; message: string | null | undefined }) {
  if (!message) return null;
  return (
    <p id={id} className="text-xs text-red-600">
      {message}
    </p>
  );
}

function ReadOnlyField({ id, label, value, multiline }: { id: string; label: string; value: string; multiline?: boolean }) {
  if (multiline) {
    // Feedback can run long: shown in full (line breaks kept), scrolling only
    // past a generous height, rather than in a fixed three-line box.
    const labelId = `${id}-label`;
    return (
      <div className="space-y-1.5">
        <p id={labelId} className="block text-sm font-medium text-gray-700">
          {label}
        </p>
        <div
          id={id}
          role="textbox"
          aria-readonly="true"
          aria-multiline="true"
          aria-labelledby={labelId}
          tabIndex={0}
          className={`${READ_ONLY_CLASS} max-h-60 overflow-y-auto whitespace-pre-wrap break-words`}
        >
          {value}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor={id} label={label} />
      <input id={id} value={value} readOnly className={READ_ONLY_CLASS} />
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="rounded-lg border border-gray-200 bg-white p-4">
      <h4 id={headingId} className="text-base font-semibold text-gray-900">
        {title}
      </h4>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export default function ProbationReviewModal({
  confirmationId,
  view,
  employeeLabel,
  onClose,
  onSubmitted,
}: ProbationReviewModalProps) {
  const [bundle, setBundle] = useState<ProbationReviewBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [skillEvaluations, setSkillEvaluations] = useState<SkillEvaluation[]>([]);
  const [recommendation, setRecommendation] = useState('');
  const [extensionMonths, setExtensionMonths] = useState('');
  const [lastWorkingDate, setLastWorkingDate] = useState('');
  const [feedback, setFeedback] = useState('');
  // Set when 1Office's suggested last working day couldn't be read. The field
  // still works; the manager just picks the date themselves.
  const [lastWorkingDateNote, setLastWorkingDateNote] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(NO_FIELD_ERRORS);
  const [formError, setFormError] = useState<string | null>(null);
  // The switch was turned off after the form loaded (the API answered 403
  // SUBMIT_DISABLED): treated exactly like a bundle that arrived switched off.
  const [switchedOffMessage, setSwitchedOffMessage] = useState<string | null>(null);
  // A submit ended in a 502 after which 1Office may hold the decision: Submit
  // stays off; the message above the buttons says to reopen it later.
  const [sentUnconfirmed, setSentUnconfirmed] = useState(false);
  const [focusInvalidTick, setFocusInvalidTick] = useState(0);

  const idPrefix = useId();
  const fieldId = (name: string) => `${idPrefix}-${name}`;
  const headerRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(true);

  // Latest values for callbacks that outlive the render that created them (the
  // load effect, the suggested-date read, the document key handler).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const recommendationRef = useRef(recommendation);
  recommendationRef.current = recommendation;
  const submittingRef = useRef(submitting);
  submittingRef.current = submitting;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The Portal's load(): the API runs the same 1Office read sequence and sends
  // one bundle. The form starts empty for L1; L2 starts from L1's decision.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await probationAPI.detail(confirmationId, view);
        if (cancelled) return;
        const loaded = response.data.data;
        setBundle(loaded);
        if (loaded.mode === 'l1') {
          setSkillEvaluations(
            loaded.skills.map((skill) => ({ skillId: skill.skillId, skillName: skill.skillName, scoreId: '', comment: '' })),
          );
        } else if (loaded.mode === 'l2') {
          const l1 = loaded.l1Decision;
          setRecommendation(l1?.recommendation ? String(l1.recommendation) : '');
          const months = l1?.extensionMonths ? String(l1.extensionMonths) : '';
          setExtensionMonths(EXTENSION_PERIODS.includes(months) ? months : '');
          // L1's date can be in the past by the time L2 acts: the API would refuse
          // it, so it is shown as a note to pick a new one, not filled in.
          const l1Day = toDay(l1?.lastWorkingDate);
          const bounds = loaded.lastWorkingDateBounds;
          if (!l1Day || withinBounds(l1Day, bounds?.min ?? localToday(), bounds?.max || undefined)) {
            setLastWorkingDate(l1Day);
          } else if (l1?.recommendation === 3) {
            setLastWorkingDateNote(
              `L1's last working date (${formatIsoDay(l1Day)}) is outside the dates allowed now — please pick a new one.`,
            );
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading probation confirmation:', err);
        const failure = readFailure(err);
        onCloseRef.current(failure.message ?? (failure.status === 404 ? NOT_YOURS_MESSAGE : LOAD_FAILED_MESSAGE));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [confirmationId, view]);

  // Nothing closes the form while a decision is in flight: its answer would
  // land on a closed modal and the list would never hear how it went.
  const requestClose = () => {
    if (!submittingRef.current) onCloseRef.current();
  };

  // The shared Modal takes no ARIA props, so the dialog semantics go on its
  // panel — the element wrapping the header passed in below.
  useLayoutEffect(() => {
    const panel = headerRef.current?.parentElement;
    if (!panel) return;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', `${idPrefix}-title`);
  }, [idPrefix]);

  // Focus moves into the dialog on open and back to whatever opened it on close.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    titleRef.current?.focus();
    return () => opener?.focus();
  }, []);

  // Escape closes (as the Portal's dialog does); Tab cycles inside the dialog
  // instead of wandering into the page behind the overlay.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        requestClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = headerRef.current?.parentElement;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          // :disabled (not [disabled]) also skips controls the fieldset disables mid-submit.
          'button:not(:disabled), select:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !active || !panel.contains(active);
      if (event.shiftKey && (active === first || active === titleRef.current || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // After a failed submit, take the manager to the first field that needs them.
  useEffect(() => {
    if (!focusInvalidTick) return;
    contentRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [focusInvalidTick]);

  const mode = bundle?.mode ?? 'view';
  const editable = mode === 'l1' || mode === 'l2';
  const stage = bundle?.employee.stage ?? 0;
  const noticeMessage = `As per terms of employment the employee needs to be provided a notice period of ${
    bundle?.employee.probationNoticePeriodDays ?? 0
  } days during probation.`;
  const lastWorkingDateMin = bundle?.lastWorkingDateBounds?.min ?? localToday();
  // The API sends no upper bound (null) when the notice period is 0, as the
  // Portal leaves the picker open-ended then.
  const lastWorkingDateMax = bundle?.lastWorkingDateBounds?.max || undefined;
  const switchedOffNote = bundle && !bundle.submitEnabled ? SUBMIT_DISABLED_MESSAGE : switchedOffMessage;

  const title = bundle
    ? `Employee Confirmation <${bundle.employee.name}-${bundle.employee.employeeCode ?? 'NA'}>`
    : `Employee Confirmation <${employeeLabel}>`;

  // 1Office's own last working day (notice periods applied). It only fills an
  // empty field, and only while Termination is still the recommendation.
  // A suggestion outside today's bounds (1Office can return an earlier cycle's
  // date, or one computed on the previous day) is named, not filled in.
  const fetchSuggestedLastWorkingDate = async () => {
    setLastWorkingDateNote(null);
    try {
      const response = await probationAPI.lastWorkingDate(confirmationId);
      if (!mountedRef.current || recommendationRef.current !== '3') return;
      const suggested = toDay(response.data.data?.lastWorkingDate);
      if (suggested && !withinBounds(suggested, lastWorkingDateMin, lastWorkingDateMax)) {
        setLastWorkingDateNote(
          `1Office suggested ${formatIsoDay(suggested)}, which is outside the dates allowed now — please pick a date.`,
        );
        return;
      }
      setLastWorkingDate((current) => current || suggested);
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('Error loading suggested last working date:', err);
      setLastWorkingDateNote(readFailure(err).message ?? LAST_WORKING_DATE_FAILED_MESSAGE);
    }
  };

  // As the Portal's setL1/L2RecommendationId: a new recommendation clears the
  // extension period, and the last working date unless it is Termination —
  // which then asks 1Office for the suggested date.
  const handleRecommendationChange = (value: string) => {
    setRecommendation(value);
    recommendationRef.current = value;
    setExtensionMonths('');
    if (value !== '3') setLastWorkingDate('');
    setLastWorkingDateNote(null);
    setFieldErrors((prev) => ({ ...prev, recommendation: null, extensionMonths: null, lastWorkingDate: null }));
    if (value === '3') void fetchSuggestedLastWorkingDate();
  };

  const setSkillScore = (index: number, value: string) => {
    setSkillEvaluations((prev) => prev.map((skill, i) => (i === index ? { ...skill, scoreId: value } : skill)));
    setFieldErrors((prev) => {
      if (!prev.skillScores[index]) return prev;
      const { [index]: _cleared, ...rest } = prev.skillScores;
      return { ...prev, skillScores: rest };
    });
  };

  const setSkillComment = (index: number, value: string) => {
    setSkillEvaluations((prev) => prev.map((skill, i) => (i === index ? { ...skill, comment: value } : skill)));
    setFieldErrors((prev) => {
      if (!prev.skillComments[index]) return prev;
      const { [index]: _cleared, ...rest } = prev.skillComments;
      return { ...prev, skillComments: rest };
    });
  };

  // The Portal dialog's handleSubmit checks, with its copy word for word. A
  // date outside the notice window gets the notice message too: the API would
  // refuse it anyway.
  const validate = (): FieldErrors => {
    const next: FieldErrors = { ...NO_FIELD_ERRORS, skillScores: {}, skillComments: {} };
    if (!recommendation) next.recommendation = 'Please select a recommendation';
    if (recommendation === '2' && !extensionMonths) next.extensionMonths = 'Please select the extension period.';
    if (
      recommendation === '3' &&
      (!lastWorkingDate ||
        lastWorkingDate < lastWorkingDateMin ||
        (lastWorkingDateMax !== undefined && lastWorkingDate > lastWorkingDateMax))
    ) {
      next.lastWorkingDate = noticeMessage;
    }
    if (!feedback.trim()) next.feedback = 'Please enter the feedback';
    if (mode === 'l1') {
      skillEvaluations.forEach((skill, index) => {
        if (!skill.scoreId) next.skillScores[index] = 'Please select the score';
        if (!skill.comment.trim()) next.skillComments[index] = 'Please enter comment';
      });
    }
    return next;
  };

  // The Portal store's submit() guard: the budget must carry skills. finalOnSubmit
  // is its isLatePhase (this submit is confStage 4).
  const skillsMissing = (loaded: ProbationReviewBundle) => {
    const l1Only = loaded.employee.probationApprovalLevel === 1;
    if (!loaded.finalOnSubmit || l1Only) return skillEvaluations.length === 0;
    return loaded.mode === 'l2' && loaded.scores.length === 0;
  };

  const handleSubmit = async () => {
    if (!bundle || !editable || switchedOffNote || sentUnconfirmed || submitting) return;

    const nextErrors = validate();
    setFieldErrors(nextErrors);
    setFormError(null);
    if (hasFieldErrors(nextErrors)) {
      setFocusInvalidTick((tick) => tick + 1);
      return;
    }
    if (skillsMissing(bundle)) {
      setFormError(NO_SKILLS_MESSAGE);
      return;
    }

    const chosen = Number(recommendation) as ProbationRecommendation;
    const input: ProbationDecisionInput = {
      recommendation: chosen,
      feedback: feedback.trim(),
      expected: bundle.expected,
    };
    if (chosen === 2) input.extensionMonths = Number(extensionMonths) as 1 | 2 | 3;
    if (chosen === 3) input.lastWorkingDate = lastWorkingDate;
    if (mode === 'l1') {
      input.scores = skillEvaluations.map((skill) => ({
        skillId: skill.skillId,
        scoreId: Number(skill.scoreId) as 1 | 2 | 3 | 4,
        comments: skill.comment,
      }));
    }

    setSubmitting(true);
    submittingRef.current = true;
    try {
      const response = await probationAPI.submit(confirmationId, input);
      submittingRef.current = false;
      onSubmitted(response.data.data);
    } catch (err) {
      submittingRef.current = false;
      console.error('Error submitting probation confirmation:', err);
      if (!mountedRef.current) return;
      const failure = readFailure(err);
      if (failure.status === 422) {
        const { fields, other } = splitServerErrors(failure.errors, skillEvaluations);
        setFieldErrors(fields);
        const lines = [failure.message, ...other].filter((line): line is string => Boolean(line));
        setFormError(Array.from(new Set(lines)).join(' ') || SUBMIT_FAILED_MESSAGE);
        setFocusInvalidTick((tick) => tick + 1);
      } else if (failure.status === 404 || failure.status === 409) {
        // Already actioned, not this user's step any more, or changed since it
        // was opened: nothing to retry here. The parent re-reads the list.
        onCloseRef.current(failure.message ?? (failure.status === 404 ? NOT_YOURS_MESSAGE : STALE_MESSAGE));
      } else if (failure.code === 'SUBMIT_DISABLED') {
        setSwitchedOffMessage(failure.message ?? SUBMIT_DISABLED_MESSAGE);
      } else {
        // 502 (1Office didn't save it, saved part of it, or we can't tell), 403
        // identity, anything else: stay open with the server's words so the
        // manager can act on them. After a 502 that may have reached 1Office,
        // Submit stays off; one that sent nothing (UPSTREAM_UNAVAILABLE) can retry.
        if (failure.code && SENT_UNCONFIRMED_CODES.includes(failure.code)) setSentUnconfirmed(true);
        setFormError(failure.message ?? SUBMIT_FAILED_MESSAGE);
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const renderKpis = () => (
    <div className="overflow-x-auto rounded-lg border border-gray-100">
      {/* Two short columns: headers may wrap so the table fits a phone without scrolling. */}
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/70">
            <th scope="col" className={TH_BASE_CLASS}>KPI Achievement</th>
            <th scope="col" className={TH_BASE_CLASS}>Result (In Percentage)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(bundle?.kpis.length ?? 0) === 0 ? (
            <tr>
              <td colSpan={2} className="px-4 py-6 text-center text-sm text-gray-500">No KPI result available.</td>
            </tr>
          ) : (
            bundle?.kpis.map((kpi, index) => (
              <tr key={`${kpi.kpiValidFromDate ?? 'kpi'}-${index}`}>
                <td className={TD_CLASS}>{kpiPeriodLabel(kpi.kpiValidFromDate, kpi.kpiValidToDate)}</td>
                <td className={TD_CLASS}>{kpi.earnedVariable ?? '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderSkillEvaluation = () => (
    <div className="overflow-x-auto rounded-lg border border-gray-100">
      <table className={STACK_TABLE_CLASS}>
        <thead className={STACK_THEAD_CLASS}>
          <tr className="border-b border-gray-100 bg-gray-50/70">
            <th scope="col" className={TH_CLASS}>Skills</th>
            <th scope="col" className={TH_CLASS}>{SCORE_COLUMN_LABEL}</th>
            <th scope="col" className={TH_CLASS}>Comments</th>
          </tr>
        </thead>
        <tbody className={STACK_TBODY_CLASS}>
          {skillEvaluations.length === 0 ? (
            <tr className="block sm:table-row">
              <td colSpan={3} className={STACK_EMPTY_TD_CLASS}>No skills available.</td>
            </tr>
          ) : (
            skillEvaluations.map((skill, index) => {
              const scoreError = fieldErrors.skillScores[index];
              const commentError = fieldErrors.skillComments[index];
              const scoreErrorId = fieldId(`score-${index}-error`);
              const commentErrorId = fieldId(`comment-${index}-error`);
              return (
                <tr key={`${skill.skillId}-${index}`} className={STACK_TR_CLASS}>
                  {/* Top padding = the cell's own + a control's border and padding, so the
                      name lines up with the text in the score select and comment box. */}
                  <td className={`${STACK_NAME_TD_CLASS} sm:pt-[1.3125rem]`}>{skill.skillName}</td>
                  <td data-label={SCORE_COLUMN_LABEL} className={`${STACK_TD_CLASS} space-y-1`}>
                    <select
                      id={fieldId(`score-${index}`)}
                      aria-label={`Evaluation score for ${skill.skillName}`}
                      value={skill.scoreId}
                      onChange={(event) => setSkillScore(index, event.target.value)}
                      aria-invalid={Boolean(scoreError)}
                      aria-describedby={describedBy(scoreError && scoreErrorId)}
                      className={`${controlClass(Boolean(scoreError))} max-w-[10rem]`}
                    >
                      <option value="">Select</option>
                      {EVALUATION_SCORES.map((score) => (
                        <option key={score} value={score}>
                          {score}
                        </option>
                      ))}
                    </select>
                    <FieldError id={scoreErrorId} message={scoreError} />
                  </td>
                  <td data-label="Comments" className={`${STACK_TD_CLASS} space-y-1`}>
                    <input
                      id={fieldId(`comment-${index}`)}
                      aria-label={`Comments for ${skill.skillName}`}
                      value={skill.comment}
                      onChange={(event) => setSkillComment(index, event.target.value)}
                      placeholder="Comment"
                      aria-invalid={Boolean(commentError)}
                      aria-describedby={describedBy(commentError && commentErrorId)}
                      className={controlClass(Boolean(commentError))}
                    />
                    <FieldError id={commentErrorId} message={commentError} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  const renderScores = () => {
    const scores = (bundle?.scores ?? []).filter((score) => score.skillName);
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className={STACK_TABLE_CLASS}>
          <thead className={STACK_THEAD_CLASS}>
            <tr className="border-b border-gray-100 bg-gray-50/70">
              <th scope="col" className={TH_CLASS}>Skills</th>
              <th scope="col" className={TH_CLASS}>{SCORE_COLUMN_LABEL}</th>
              <th scope="col" className={TH_CLASS}>Comments</th>
            </tr>
          </thead>
          <tbody className={STACK_TBODY_CLASS}>
            {scores.length === 0 ? (
              <tr className="block sm:table-row">
                <td colSpan={3} className={STACK_EMPTY_TD_CLASS}>
                  No evaluation score available.
                </td>
              </tr>
            ) : (
              scores.map((score, index) => (
                <tr key={`${score.skillName}-${index}`} className={STACK_TR_CLASS}>
                  <td className={`${STACK_NAME_TD_CLASS} sm:pt-3`}>{score.skillName}</td>
                  <td data-label={SCORE_COLUMN_LABEL} className={STACK_TD_CLASS}>{score.scoreName ?? '—'}</td>
                  <td data-label="Comments" className={`${STACK_TD_CLASS} whitespace-pre-wrap break-words`}>
                    {score.comments || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRecommendationSelect = () => {
    const errorId = fieldId('recommendation-error');
    return (
      <div className="space-y-1.5">
        <FieldLabel htmlFor={fieldId('recommendation')} label="Recommendation" required />
        <select
          id={fieldId('recommendation')}
          value={recommendation}
          onChange={(event) => handleRecommendationChange(event.target.value)}
          aria-invalid={Boolean(fieldErrors.recommendation)}
          aria-describedby={describedBy(fieldErrors.recommendation && errorId)}
          className={controlClass(Boolean(fieldErrors.recommendation))}
        >
          <option value="">Select</option>
          {RECOMMENDATIONS.map((option) => (
            <option key={option.id} value={String(option.id)}>
              {option.label}
            </option>
          ))}
        </select>
        <FieldError id={errorId} message={fieldErrors.recommendation} />
      </div>
    );
  };

  const renderExtensionSelect = () => {
    if (recommendation !== '2') return null;
    const errorId = fieldId('extension-error');
    return (
      <div className="space-y-1.5">
        <FieldLabel htmlFor={fieldId('extension')} label="Extension Period" required />
        <select
          id={fieldId('extension')}
          value={extensionMonths}
          onChange={(event) => {
            setExtensionMonths(event.target.value);
            setFieldErrors((prev) => ({ ...prev, extensionMonths: null }));
          }}
          aria-invalid={Boolean(fieldErrors.extensionMonths)}
          aria-describedby={describedBy(fieldErrors.extensionMonths && errorId)}
          className={controlClass(Boolean(fieldErrors.extensionMonths))}
        >
          <option value="">Select</option>
          {EXTENSION_PERIODS.map((period) => (
            <option key={period} value={period}>
              {monthsLabel(period)}
            </option>
          ))}
        </select>
        <FieldError id={errorId} message={fieldErrors.extensionMonths} />
      </div>
    );
  };

  const renderLastWorkingDate = () => {
    if (recommendation !== '3') return null;
    const helpId = fieldId('last-working-date-help');
    const errorId = fieldId('last-working-date-error');
    return (
      <div className="space-y-1.5">
        <FieldLabel htmlFor={fieldId('last-working-date')} label="Last Working Date" required />
        <input
          id={fieldId('last-working-date')}
          type="date"
          value={lastWorkingDate}
          min={lastWorkingDateMin}
          max={lastWorkingDateMax}
          onChange={(event) => {
            setLastWorkingDate(event.target.value);
            setFieldErrors((prev) => ({ ...prev, lastWorkingDate: null }));
          }}
          aria-invalid={Boolean(fieldErrors.lastWorkingDate)}
          aria-describedby={describedBy(helpId, fieldErrors.lastWorkingDate && errorId)}
          className={`${controlClass(Boolean(fieldErrors.lastWorkingDate))} sm:max-w-xs`}
        />
        <p id={helpId} className="text-xs text-gray-500">
          {noticeMessage}
        </p>
        {lastWorkingDateNote && <p className="text-xs text-amber-700">{lastWorkingDateNote}</p>}
        <FieldError id={errorId} message={fieldErrors.lastWorkingDate} />
      </div>
    );
  };

  const renderFeedback = () => {
    const errorId = fieldId('feedback-error');
    return (
      <div className="space-y-1.5">
        <FieldLabel htmlFor={fieldId('feedback')} label="Feedback" required />
        <textarea
          id={fieldId('feedback')}
          value={feedback}
          onChange={(event) => {
            setFeedback(event.target.value);
            setFieldErrors((prev) => ({ ...prev, feedback: null }));
          }}
          rows={4}
          aria-invalid={Boolean(fieldErrors.feedback)}
          aria-describedby={describedBy(fieldErrors.feedback && errorId)}
          className={`${controlClass(Boolean(fieldErrors.feedback))} min-h-[90px]`}
        />
        <FieldError id={errorId} message={fieldErrors.feedback} />
      </div>
    );
  };

  // A recorded decision's extension period / last working day, when it has one.
  const renderSnapshotDetails = (snapshot: ProbationDecisionSnapshot | null, prefix: string, key: string) => (
    <>
      {snapshot?.recommendation === 2 && snapshot.extensionMonths ? (
        <ReadOnlyField
          id={fieldId(`${key}-extension`)}
          label={`${prefix}Extension Period`}
          value={monthsLabel(snapshot.extensionMonths)}
        />
      ) : null}
      {snapshot?.recommendation === 3 && snapshot.lastWorkingDate ? (
        <ReadOnlyField
          id={fieldId(`${key}-last-working-date`)}
          label={`${prefix}Last Working Date`}
          value={displayDay(snapshot.lastWorkingDate)}
        />
      ) : null}
    </>
  );

  const renderRecommendationSection = () => {
    if (!bundle) return null;
    const l1 = bundle.l1Decision;

    if (mode === 'l1') {
      return (
        <div className="flex flex-col gap-4">
          {renderRecommendationSelect()}
          {renderLastWorkingDate()}
          {renderExtensionSelect()}
          {renderFeedback()}
        </div>
      );
    }

    if (mode === 'l2') {
      return (
        <div className="flex flex-col gap-4">
          <ReadOnlyField id={fieldId('l1-recommendation')} label="L1 Recommendation" value={recommendationLabel(l1?.recommendation)} />
          {/* Read-only, so still ONE Extension Period control: L2's own, below. */}
          {renderSnapshotDetails(l1, 'L1 ', 'l2-l1')}
          <ReadOnlyField id={fieldId('l1-feedback')} label="L1 Feedback" value={l1?.feedback || '—'} multiline />
          <div className="flex flex-col gap-4 border-t border-gray-100 pt-4">
            {renderRecommendationSelect()}
            {renderExtensionSelect()}
            {renderLastWorkingDate()}
            {renderFeedback()}
          </div>
        </div>
      );
    }

    // View. As the Portal: once L1 has decided (stage 3+) their fields read
    // "L1 …", and from stage 4 the final decision follows — falling back to
    // L1's recommendation when 1Office has no stage-4 entry.
    const prefix = stage >= 3 ? 'L1 ' : '';
    const final = bundle.finalDecision;
    return (
      <div className="flex flex-col gap-4">
        <ReadOnlyField id={fieldId('view-l1-recommendation')} label={`${prefix}Recommendation`} value={recommendationLabel(l1?.recommendation)} />
        {renderSnapshotDetails(l1, prefix, 'view-l1')}
        <ReadOnlyField id={fieldId('view-l1-feedback')} label={`${prefix}Feedback`} value={l1?.feedback || '—'} multiline />
        {stage >= 4 && (
          <div className="flex flex-col gap-4 border-t border-gray-100 pt-4">
            <ReadOnlyField
              id={fieldId('view-final-recommendation')}
              label="Recommendation"
              value={recommendationLabel(final?.recommendation ?? l1?.recommendation)}
            />
            {renderSnapshotDetails(final, '', 'view-final')}
            <ReadOnlyField id={fieldId('view-final-feedback')} label="Feedback" value={final?.feedback || '—'} multiline />
          </div>
        )}
      </div>
    );
  };

  const secondaryButtonClass =
    'rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';
  const switchedOffNoteId = fieldId('switched-off-note');

  const footer = (
    <div className="flex w-full flex-col gap-3">
      {formError && (
        <div role="alert">
          <ModalFormErrorBanner message={formError} />
        </div>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
        {bundle && editable ? (
          <>
            {switchedOffNote && (
              <p id={switchedOffNoteId} className="text-xs text-gray-500 sm:mr-auto">
                {switchedOffNote}
              </p>
            )}
            <button type="button" onClick={requestClose} disabled={submitting} className={secondaryButtonClass}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting || Boolean(switchedOffNote) || sentUnconfirmed}
              aria-describedby={describedBy(switchedOffNote && switchedOffNoteId)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </button>
          </>
        ) : (
          <button type="button" onClick={requestClose} className={secondaryButtonClass}>
            Close
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={requestClose}
      title={title}
      size="xl"
      footer={footer}
      headerContent={
        <div
          ref={headerRef}
          className="flex items-start justify-between gap-3 rounded-t-lg border-b border-gray-200 bg-white px-6 py-4"
        >
          <h3
            id={`${idPrefix}-title`}
            ref={titleRef}
            tabIndex={-1}
            className="min-w-0 break-words text-lg font-semibold text-gray-900 focus:outline-none"
          >
            {title}
          </h3>
          <button
            type="button"
            onClick={requestClose}
            disabled={submitting}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
      }
    >
      <div ref={contentRef} className="relative min-h-[12rem]" aria-busy={loading}>
        {bundle && (
          // min-w-0: a fieldset is min-content wide by default, which would stop
          // the tables from scrolling inside the modal at narrow widths.
          <fieldset disabled={submitting} className="flex min-w-0 flex-col gap-4">
            <SectionCard title="Employee Details">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ReadOnlyField id={fieldId('project')} label="Project" value={bundle.employee.project || '—'} />
                <ReadOnlyField id={fieldId('designation')} label="Designation" value={bundle.employee.designation || '—'} />
                <ReadOnlyField id={fieldId('doj')} label="Date of Joining" value={displayDay(bundle.employee.dateOfJoining)} />
                <ReadOnlyField
                  id={fieldId('confirmation-date')}
                  label="Date of Confirmation"
                  value={displayDay(bundle.employee.confirmationDate)}
                />
              </div>
            </SectionCard>

            <SectionCard title="KPI Result">{renderKpis()}</SectionCard>

            <SectionCard title="Evaluation">{mode === 'l1' ? renderSkillEvaluation() : renderScores()}</SectionCard>

            <SectionCard title="Recommendation & Feedback">{renderRecommendationSection()}</SectionCard>
          </fieldset>
        )}
        {loading && <BlockLoader label="Loading…" className="absolute inset-0 z-10 bg-white/80" />}
      </div>
    </Modal>
  );
}
